import { useState, useCallback } from "react";
import { ITEMS_PER_PAGE } from "../utils/applicationConstants";

/**
 * Custom hook for managing application table UI state
 * Now supports server-side filtering with explicit "Apply Filters" pattern
 * Centralizes all state management logic
 */
export const useApplicationState = () => {
  // Selection state
  const [selectedIds, setSelectedIds] = useState([]);

  // Filter state (pending - not applied to server yet)
  const [statusFilter, setStatusFilter] = useState("All");
  const [locationFilter, setLocationFilter] = useState("All");
  const [roleFilter, setRoleFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  // Server-side filter state (applied filters sent to API)
  const [appliedFilters, setAppliedFilters] = useState({});
  const [appliedSearch, setAppliedSearch] = useState(null);

  // Sort state
  const [sortField, setSortField] = useState("applicationDate");
  const [sortOrder, setSortOrder] = useState("desc");

  // Row editing state
  const [changedRows, setChangedRows] = useState({}); // {id: {field: {original, current}}}
  const [expandedRows, setExpandedRows] = useState({}); // {id: boolean}

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(ITEMS_PER_PAGE);

  // Selection helpers
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const selectAll = useCallback((ids) => {
    setSelectedIds(ids);
  }, []);

  // Sort helpers
  const toggleSort = useCallback((field) => {
    setSortField((prevField) => {
      if (prevField === field) {
        setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
        return field;
      } else {
        setSortField(field);
        setSortOrder("asc");
        return field;
      }
    });
  }, []);

  // Row editing helpers
  const markRowChanged = useCallback((id, field, original, current) => {
    setChangedRows((prev) => {
      const rowChanges = prev[id] || {};
      if (!rowChanges[field]) {
        rowChanges[field] = { original };
      }
      rowChanges[field].current = current;
      return { ...prev, [id]: rowChanges };
    });
  }, []);

  const clearRowChanges = useCallback((id) => {
    setChangedRows((prev) => {
      const newPrev = { ...prev };
      delete newPrev[id];
      return newPrev;
    });
  }, []);

  const getRowChanges = useCallback((id) => {
    return changedRows[id];
  }, [changedRows]);

  // Row expansion helpers
  const toggleRowExpansion = useCallback((id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const isRowExpanded = useCallback(
    (id) => {
      return !!expandedRows[id];
    },
    [expandedRows],
  );

  // Pagination helpers
  const goToPage = useCallback((page) => {
    setCurrentPage(page);
    setExpandedRows({}); // Close all expanded rows when changing pages
  }, []);

  const resetPagination = useCallback(() => {
    setCurrentPage(1);
  }, []);

  // Server-side filter helpers
  const applyFilters = useCallback(() => {
    const filters = {};

    // Status filter
    if (statusFilter && statusFilter !== "All") {
      if (statusFilter === "archived") {
        filters.archivedOnly = true;
      } else {
        filters.status = [statusFilter];
        filters.includeArchived = false; // Exclude archived by default
      }
    } else {
      // "All" excludes archived by default
      filters.includeArchived = false;
    }

    // Location filter
    if (locationFilter && locationFilter !== "All") {
      filters.cities = [locationFilter];
    }

    // Role filter
    if (roleFilter && roleFilter !== "All") {
      // Note: API doesn't have roleTitle filter yet, need to use search
      // For now, we'll skip this or add to search query
    }

    setAppliedFilters(filters);

    // Search query
    if (searchQuery && searchQuery.trim()) {
      setAppliedSearch({
        query: searchQuery.trim(),
        fields: ["roleTitle", "companyName", "jobLocationCity"],
        caseSensitive: false,
      });
    } else {
      setAppliedSearch(null);
    }

    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [statusFilter, locationFilter, roleFilter, searchQuery]);

  const clearAllFilters = useCallback(() => {
    setStatusFilter("All");
    setLocationFilter("All");
    setRoleFilter("All");
    setSearchQuery("");
    setAppliedFilters({});
    setAppliedSearch(null);
    setCurrentPage(1);
  }, []);

  return {
    // Selection
    selectedIds,
    toggleSelect,
    clearSelection,
    selectAll,

    // Filters (pending UI state)
    statusFilter,
    setStatusFilter,
    locationFilter,
    setLocationFilter,
    roleFilter,
    setRoleFilter,
    searchQuery,
    setSearchQuery,

    // Server-side filters (applied)
    appliedFilters,
    appliedSearch,
    applyFilters,
    clearAllFilters,

    // Sorting
    sortField,
    sortOrder,
    toggleSort,

    // Row editing
    changedRows,
    markRowChanged,
    clearRowChanges,
    getRowChanges,

    // Row expansion
    expandedRows,
    toggleRowExpansion,
    isRowExpanded,

    // Pagination
    currentPage,
    itemsPerPage,
    goToPage,
    resetPagination,
  };
};
